import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, PlayCircle, ListChecks, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/button-variants";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert } from "@/components/ui/alert";
import { ApiError, getHistory } from "@/lib/api";
import { formatTimestamp } from "@lib/format";
import type { VideoHistoryItem } from "@lib/domain";

const PAGE_SIZE = 12;

function HistoryCard({ item }: { item: VideoHistoryItem }) {
  return (
    <Card className="flex flex-col overflow-hidden">
      <div className="relative aspect-video w-full bg-muted">
        {item.thumbnail ? (
          <img
            src={item.thumbnail}
            alt=""
            loading="lazy"
            className="size-full object-cover"
          />
        ) : (
          <div className="flex size-full items-center justify-center text-muted-foreground">
            <FileText className="size-8" />
          </div>
        )}
        {item.durationSeconds !== null && (
          <span className="absolute bottom-1.5 right-1.5 rounded bg-black/75 px-1.5 py-0.5 text-xs font-medium text-white">
            {formatTimestamp(item.durationSeconds)}
          </span>
        )}
      </div>
      <CardContent className="flex flex-1 flex-col gap-3 p-4">
        <div className="space-y-1">
          <h2 className="line-clamp-2 font-medium leading-snug">{item.title ?? "Untitled"}</h2>
          <p className="text-xs text-muted-foreground">
            {item.channel ?? "Your text"}
            {item.isText ? " · text quiz" : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-1">
          {item.levels.map((level) => (
            <Badge key={level.level} variant="outline" title={level.tagline}>
              {level.name} · {level.questionCount}
            </Badge>
          ))}
        </div>
        <div className="mt-auto flex gap-2">
          <Link
            to={`/quiz/${item.videoId}${item.isText ? "?mode=text" : ""}`}
            className={buttonVariants({ size: "sm", className: "flex-1" })}
          >
            <ListChecks />
            Quiz
          </Link>
          {!item.isText && (
            <Link
              to={`/watch/${item.videoId}`}
              className={buttonVariants({ size: "sm", variant: "secondary", className: "flex-1" })}
            >
              <PlayCircle />
              Watch
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function HistorySkeleton() {
  return (
    <Card className="overflow-hidden">
      <Skeleton className="aspect-video w-full rounded-none" />
      <CardContent className="space-y-3 p-4">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-8 w-full" />
      </CardContent>
    </Card>
  );
}

export function HistoryPage() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<VideoHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    getHistory({ search, limit: PAGE_SIZE })
      .then((page) => {
        if (requestId.current !== id) return;
        setItems(page.items);
        setTotal(page.total);
        setCursor(page.nextCursor);
        setHasMore(page.hasMore);
      })
      .catch((err) => {
        if (requestId.current !== id) return;
        setItems([]);
        setError(err instanceof ApiError ? err.message : "Could not load quizzes. Try again.");
      })
      .finally(() => {
        if (requestId.current === id) setLoading(false);
      });
  }, [search]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await getHistory({ search, limit: PAGE_SIZE, cursor });
      setItems((prev) => [...prev, ...page.items]);
      setTotal(page.total);
      setCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load more quizzes.");
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Browse quizzes</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {loading ? "Loading…" : `${total} ${total === 1 ? "quiz" : "quizzes"} ready to play.`}
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search by title or channel"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {error && <Alert variant="destructive">{error}</Alert>}

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <HistorySkeleton key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <ListChecks className="size-8 text-muted-foreground" />
            <p className="font-medium">{search ? "No quizzes match your search." : "No quizzes yet."}</p>
            <p className="text-sm text-muted-foreground">
              {search ? "Try a different title or channel." : "Start one from the home page."}
            </p>
            {search ? (
              <Button variant="outline" size="sm" onClick={() => setSearchInput("")}>
                Clear search
              </Button>
            ) : (
              <Link to="/" className={buttonVariants({ size: "sm" })}>
                Create a quiz
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((item) => (
              <HistoryCard key={item.videoId} item={item} />
            ))}
          </div>
          {hasMore && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? <Loader2 className="animate-spin" /> : null}
                Load more
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
